import { createLocalProjection } from "@plotlibre/geometry";
import { SelectionTransformError, SelectionTransformSession, } from "@plotlibre/interaction";
import { MapLibrePlotRenderer } from "./renderer.js";
import { MapLibreSelectionTransformOverlay, } from "./selection-transform-overlay.js";
const CAMERA_START_EVENTS = [
    "movestart",
    "zoomstart",
    "rotatestart",
    "pitchstart",
];
const HARD_CANCEL_EVENTS = ["style.load", "resize"];
const MINIMUM_START_RADIUS_PIXELS = 4;
const MINIMUM_VISUAL_FRAME_EDGE_PIXELS = 24;
/**
 * Explicit one-shot MapLibre adapter for complete-selection rotation/scale.
 *
 * Canonical math and Registry preflight live in @plotlibre/interaction. This
 * controller owns only map projection, DOM handle input, transient rendering,
 * lifecycle cancellation and one delegated atomic command commit.
 */
export class MapLibreSelectionTransformInteraction {
    #map;
    #registry;
    #store;
    #selection;
    #renderer;
    #overlay;
    #callbacks;
    #listeners = new Set();
    #unsubscribeStore;
    #unsubscribeSelection;
    #kind;
    #session;
    #selectionSnapshot;
    #externalRejection;
    #ignoredOverlayPointerId;
    #dragPanWasEnabled;
    #revision = 0;
    #applying = false;
    #destroyed = false;
    #onCanvasPointerDown = (event) => {
        if (this.#destroyed || !this.isActive || this.isGestureActive)
            return;
        consumePointerEvent(event);
        this.cancel();
    };
    #onOverlayPointerDown = (event) => {
        if (this.#destroyed ||
            event.kind !== this.#kind ||
            !this.#session ||
            this.#callbacks.isDrawing?.() ||
            this.#callbacks.isRegionActive?.() ||
            this.#callbacks.isTranslating?.()) {
            return;
        }
        const screenPivot = projectFramePivot(this.#map, this.#session.frame);
        if (!screenPivot) {
            this.#ignoredOverlayPointerId = event.pointerId;
            this.#externalRejection = createAdapterRejection("SELECTION_TRANSFORM_POINTER_INVALID", "Map adapter could not project the selection-transform pivot.", this.#selectionSnapshot?.selectedIds ?? []);
            this.#render();
            this.#emit();
            consumeOverlayEvent(event);
            return;
        }
        if (Math.hypot(event.point.x - screenPivot.x, event.point.y - screenPivot.y) < MINIMUM_START_RADIUS_PIXELS) {
            this.#ignoredOverlayPointerId = event.pointerId;
            this.#externalRejection = createAdapterRejection("SELECTION_TRANSFORM_POINTER_RADIUS_TOO_SMALL", `Selection transform must start at least ${MINIMUM_START_RADIUS_PIXELS} CSS pixels from the pivot.`, this.#selectionSnapshot?.selectedIds ?? []);
            this.#render();
            this.#emit();
            consumeOverlayEvent(event);
            return;
        }
        const local = this.#toLocalPoint(event.point);
        if (!local) {
            this.#ignoredOverlayPointerId = event.pointerId;
            this.#externalRejection = createAdapterRejection("SELECTION_TRANSFORM_POINTER_INVALID", "Map adapter could not convert the transform pointer into local metres.", this.#selectionSnapshot?.selectedIds ?? []);
            this.#render();
            this.#emit();
            consumeOverlayEvent(event);
            return;
        }
        this.#ignoredOverlayPointerId = undefined;
        const snapshot = this.#session.pointerDown(local);
        this.#externalRejection = undefined;
        if (snapshot.status === "active")
            this.#disableDragPan();
        this.#renderer.clearHandles();
        this.#render();
        this.#emit();
        consumeOverlayEvent(event);
    };
    #onOverlayPointerMove = (event) => {
        if (event.pointerId === this.#ignoredOverlayPointerId) {
            consumeOverlayEvent(event);
            return;
        }
        if (event.kind !== this.#kind ||
            !this.#session ||
            !this.isGestureActive) {
            return;
        }
        const local = this.#toLocalPoint(event.point);
        if (!local) {
            this.#externalRejection = createAdapterRejection("SELECTION_TRANSFORM_POINTER_INVALID", "Map adapter could not convert the transform pointer into local metres.", this.#selectionSnapshot?.selectedIds ?? []);
            this.#render();
            this.#emit();
            consumeOverlayEvent(event);
            return;
        }
        this.#session.pointerMove(local);
        this.#externalRejection = undefined;
        this.#renderPreview();
        this.#render();
        this.#emit();
        consumeOverlayEvent(event);
    };
    #onOverlayPointerUp = (event) => {
        if (event.pointerId === this.#ignoredOverlayPointerId) {
            this.#ignoredOverlayPointerId = undefined;
            consumeOverlayEvent(event);
            return;
        }
        if (event.kind !== this.#kind || !this.#session)
            return;
        const local = this.#toLocalPoint(event.point);
        if (!local) {
            this.#externalRejection = createAdapterRejection("SELECTION_TRANSFORM_POINTER_INVALID", "Map adapter could not convert the transform pointer into local metres.", this.#selectionSnapshot?.selectedIds ?? []);
            this.#restoreDragPan();
            this.#render();
            this.#emit();
            consumeOverlayEvent(event);
            return;
        }
        const completion = this.#session.pointerUp(local);
        this.#restoreDragPan();
        consumeOverlayEvent(event);
        if (!completion.completed) {
            if (completion.noop) {
                this.#exitMode();
                return;
            }
            this.#externalRejection = completion.rejection;
            this.#renderPreview();
            this.#render();
            this.#emit();
            return;
        }
        const capturedSelection = this.#selectionSnapshot;
        if (!capturedSelection) {
            this.#externalRejection = createAdapterRejection("SELECTION_TRANSFORM_TRANSACTION_INVALID", "Selection transform lost its captured selection snapshot.", completion.originals.map((feature) => feature.id));
            this.#rearmFromCurrentSelection();
            return;
        }
        try {
            this.#applying = true;
            try {
                this.#callbacks.commit(completion, capturedSelection);
            }
            finally {
                this.#applying = false;
            }
            this.#exitMode();
        }
        catch (error) {
            this.#externalRejection = createAdapterRejection("SELECTION_TRANSFORM_TRANSACTION_INVALID", error instanceof Error
                ? error.message
                : "Selection transform transaction failed.", capturedSelection.selectedIds, error);
            this.#rearmFromCurrentSelection();
        }
    };
    #onOverlayPointerCancel = (event) => {
        if (event.pointerId === this.#ignoredOverlayPointerId) {
            this.#ignoredOverlayPointerId = undefined;
            consumeOverlayEvent(event);
            return;
        }
        if (!this.isActive)
            return;
        consumeOverlayEvent(event);
        this.cancel();
    };
    #onKeyDown = (event) => {
        if (event.key !== "Escape" || !this.isActive)
            return;
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
        this.cancel();
    };
    #onCameraStart = () => {
        if (this.isGestureActive)
            this.cancel();
    };
    #onHardCancel = () => {
        if (this.isActive)
            this.cancel();
    };
    #onRender = () => {
        if (this.isActive && !this.isGestureActive)
            this.#render();
    };
    constructor(map, registry, store, selection, renderer, options) {
        this.#map = map;
        this.#registry = registry;
        this.#store = store;
        this.#selection = selection;
        this.#renderer = renderer;
        this.#callbacks = options.callbacks;
        this.#overlay = options.overlay ?? new MapLibreSelectionTransformOverlay(map.getContainer?.());
        this.#overlay.setHandlers({
            pointerDown: this.#onOverlayPointerDown,
            pointerMove: this.#onOverlayPointerMove,
            pointerUp: this.#onOverlayPointerUp,
            pointerCancel: this.#onOverlayPointerCancel,
        });
        const canvas = map.getCanvas();
        canvas.addEventListener("pointerdown", this.#onCanvasPointerDown, {
            capture: true,
        });
        canvas.addEventListener("keydown", this.#onKeyDown, { capture: true });
        for (const type of CAMERA_START_EVENTS)
            map.on(type, this.#onCameraStart);
        for (const type of HARD_CANCEL_EVENTS)
            map.on(type, this.#onHardCancel);
        map.on("render", this.#onRender);
        this.#unsubscribeStore = store.subscribe(() => {
            if (!this.#applying && this.isActive)
                this.cancel();
        });
        this.#unsubscribeSelection = selection.subscribe(() => {
            if (!this.#applying && this.isActive)
                this.cancel();
        });
    }
    start(kind) {
        this.cancel();
        this.#callbacks.cancelRegion?.();
        this.#callbacks.cancelTranslation?.();
        this.#callbacks.cancelDrawing?.();
        this.#kind = kind;
        this.#externalRejection = undefined;
        this.#ignoredOverlayPointerId = undefined;
        const selected = new Set(this.#selection.selectedIds);
        const originals = this.#store.list().filter((feature) => selected.has(feature.id));
        this.#selectionSnapshot = this.#selection.snapshot();
        try {
            this.#session = new SelectionTransformSession(kind, originals, this.#registry);
            this.#renderer.clearHandles();
            this.#renderCurrentSelection();
            this.#render();
        }
        catch (error) {
            this.#session = undefined;
            this.#externalRejection = toAdapterRejection(error, this.#selectionSnapshot.selectedIds);
            this.#overlay.clear();
            this.#syncCurrentSelection();
        }
        this.#revision += 1;
        this.#emit();
        this.#map.getCanvas().focus?.();
        return this.snapshot;
    }
    cancel() {
        if (!this.isActive)
            return false;
        this.#restoreDragPan();
        this.#kind = undefined;
        this.#session = undefined;
        this.#selectionSnapshot = undefined;
        this.#externalRejection = undefined;
        this.#ignoredOverlayPointerId = undefined;
        this.#overlay.clear();
        this.#syncCurrentSelection();
        this.#revision += 1;
        this.#emit();
        return true;
    }
    get snapshot() {
        const sessionSnapshot = this.#session?.snapshot();
        const rejection = this.#externalRejection ?? sessionSnapshot?.rejection;
        const status = this.#kind === undefined
            ? "idle"
            : rejection !== undefined || sessionSnapshot?.status === "rejected"
                ? "rejected"
                : sessionSnapshot?.status === "active"
                    ? "active"
                    : "armed";
        return Object.freeze({
            status,
            ...(this.#kind !== undefined ? { kind: this.#kind } : {}),
            selectedIds: Object.freeze([
                ...(this.#selectionSnapshot?.selectedIds ?? []),
            ]),
            ...(sessionSnapshot?.frame.pivot !== undefined
                ? { pivot: Object.freeze([...sessionSnapshot.frame.pivot]) }
                : {}),
            ...(sessionSnapshot?.clockwiseDegrees !== undefined
                ? { clockwiseDegrees: sessionSnapshot.clockwiseDegrees }
                : {}),
            ...(sessionSnapshot?.scaleFactor !== undefined
                ? { scaleFactor: sessionSnapshot.scaleFactor }
                : {}),
            ...(rejection !== undefined ? { rejection } : {}),
            revision: this.#revision + (sessionSnapshot?.revision ?? 0),
        });
    }
    get rejection() {
        return this.#externalRejection ?? this.#session?.snapshot().rejection;
    }
    get isActive() {
        return this.#kind !== undefined;
    }
    get isGestureActive() {
        return this.#session?.status === "active";
    }
    subscribe(listener) {
        this.#listeners.add(listener);
        return () => {
            this.#listeners.delete(listener);
        };
    }
    destroy() {
        if (this.#destroyed)
            return;
        this.cancel();
        this.#destroyed = true;
        this.#unsubscribeSelection();
        this.#unsubscribeStore();
        const canvas = this.#map.getCanvas();
        canvas.removeEventListener("pointerdown", this.#onCanvasPointerDown, {
            capture: true,
        });
        canvas.removeEventListener("keydown", this.#onKeyDown, { capture: true });
        for (const type of CAMERA_START_EVENTS)
            this.#map.off(type, this.#onCameraStart);
        for (const type of HARD_CANCEL_EVENTS)
            this.#map.off(type, this.#onHardCancel);
        this.#map.off("render", this.#onRender);
        this.#overlay.setHandlers(undefined);
        this.#overlay.destroy();
        this.#listeners.clear();
    }
    #renderPreview() {
        const preview = this.#session?.previewFeatures();
        if (!preview)
            return;
        this.#renderer.renderSelection(preview, this.#selectionSnapshot?.primaryId, this.#registry);
        this.#renderer.clearHandles();
    }
    #renderCurrentSelection() {
        const features = this.#selection.selectedIds
            .map((id) => this.#store.find(id))
            .filter((feature) => feature !== undefined);
        this.#renderer.renderSelection(features, this.#selection.primaryId, this.#registry);
    }
    #syncCurrentSelection() {
        this.#renderer.clearDraft();
        this.#renderCurrentSelection();
        const primaryId = this.#selection.primaryId;
        this.#renderer.renderHandles(primaryId === undefined ? undefined : this.#store.find(primaryId));
    }
    #render() {
        const session = this.#session;
        const kind = this.#kind;
        if (!session || !kind) {
            this.#overlay.clear();
            return;
        }
        const frame = projectTransformFrame(this.#map, session.frame, kind, session.snapshot().clockwiseDegrees, session.snapshot().scaleFactor, this.rejection !== undefined);
        if (!frame) {
            this.#externalRejection = createAdapterRejection("SELECTION_TRANSFORM_POINTER_INVALID", "Map adapter could not project the transform frame.", this.#selectionSnapshot?.selectedIds ?? []);
            this.#overlay.clear();
            return;
        }
        this.#overlay.render(frame);
    }
    #toLocalPoint(point) {
        const session = this.#session;
        const unproject = this.#map.unproject;
        if (!session || !unproject)
            return undefined;
        try {
            const lngLat = unproject.call(this.#map, [point.x, point.y]);
            if (!Number.isFinite(lngLat.lng) || !Number.isFinite(lngLat.lat)) {
                return undefined;
            }
            return createLocalProjection(session.frame.origin).project([
                lngLat.lng,
                lngLat.lat,
            ]);
        }
        catch {
            return undefined;
        }
    }
    #disableDragPan() {
        if (this.#dragPanWasEnabled !== undefined)
            return;
        this.#dragPanWasEnabled = this.#map.dragPan?.isEnabled?.() ?? true;
        if (this.#dragPanWasEnabled)
            this.#map.dragPan?.disable();
    }
    #restoreDragPan() {
        const wasEnabled = this.#dragPanWasEnabled;
        this.#dragPanWasEnabled = undefined;
        if (wasEnabled)
            this.#map.dragPan?.enable();
    }
    #rearmFromCurrentSelection() {
        const kind = this.#kind;
        if (!kind)
            return;
        const selected = new Set(this.#selection.selectedIds);
        const originals = this.#store.list().filter((feature) => selected.has(feature.id));
        this.#selectionSnapshot = this.#selection.snapshot();
        this.#ignoredOverlayPointerId = undefined;
        try {
            this.#session = new SelectionTransformSession(kind, originals, this.#registry);
        }
        catch (error) {
            this.#session = undefined;
            this.#externalRejection = toAdapterRejection(error, this.#selectionSnapshot.selectedIds);
        }
        this.#renderCurrentSelection();
        this.#renderer.clearHandles();
        this.#render();
        this.#revision += 1;
        this.#emit();
    }
    #exitMode() {
        this.#restoreDragPan();
        this.#kind = undefined;
        this.#session = undefined;
        this.#selectionSnapshot = undefined;
        this.#externalRejection = undefined;
        this.#ignoredOverlayPointerId = undefined;
        this.#overlay.clear();
        this.#syncCurrentSelection();
        this.#revision += 1;
        this.#emit();
    }
    #emit() {
        const snapshot = this.snapshot;
        for (const listener of [...this.#listeners])
            listener(snapshot);
    }
}
function projectTransformFrame(map, frame, kind, clockwiseDegrees, scaleFactor, rejected) {
    const project = map.project;
    if (!project)
        return undefined;
    try {
        const projection = createLocalProjection(frame.origin);
        const localCorners = [
            { x: frame.boundsMeters.minX, y: frame.boundsMeters.minY },
            { x: frame.boundsMeters.maxX, y: frame.boundsMeters.minY },
            { x: frame.boundsMeters.maxX, y: frame.boundsMeters.maxY },
            { x: frame.boundsMeters.minX, y: frame.boundsMeters.maxY },
        ];
        const projectedCorners = localCorners.map((local) => {
            const position = projection.unproject(local);
            const point = project.call(map, [position[0], position[1]]);
            return validateScreenPoint(point);
        });
        const pivot = projectFramePivot(map, frame);
        if (!pivot || projectedCorners.some((point) => !point))
            return undefined;
        const corners = ensureMinimumVisualFrame(projectedCorners, pivot, MINIMUM_VISUAL_FRAME_EDGE_PIXELS);
        const scaleHandle = corners[2];
        const topMidpoint = {
            x: (corners[2].x + corners[3].x) / 2,
            y: (corners[2].y + corners[3].y) / 2,
        };
        const outward = normalizeScreenVector({
            x: topMidpoint.x - pivot.x,
            y: topMidpoint.y - pivot.y,
        });
        const rotationHandle = {
            x: topMidpoint.x + outward.x * 28,
            y: topMidpoint.y + outward.y * 28,
        };
        return Object.freeze({
            kind,
            corners,
            pivot,
            scaleHandle,
            rotationHandle,
            label: kind === "rotate"
                ? `${(clockwiseDegrees ?? 0).toFixed(1)}°`
                : `×${(scaleFactor ?? 1).toFixed(3)}`,
            rejected,
        });
    }
    catch {
        return undefined;
    }
}
function projectFramePivot(map, frame) {
    const project = map.project;
    if (!project)
        return undefined;
    try {
        return validateScreenPoint(project.call(map, [
            frame.pivot[0],
            frame.pivot[1],
        ]));
    }
    catch {
        return undefined;
    }
}
function ensureMinimumVisualFrame(corners, pivot, minimumEdgePixels) {
    const horizontal = {
        x: corners[1].x - corners[0].x,
        y: corners[1].y - corners[0].y,
    };
    const vertical = {
        x: corners[3].x - corners[0].x,
        y: corners[3].y - corners[0].y,
    };
    const horizontalLength = Math.hypot(horizontal.x, horizontal.y);
    const verticalLength = Math.hypot(vertical.x, vertical.y);
    let horizontalUnit = horizontalLength > 1e-9
        ? { x: horizontal.x / horizontalLength, y: horizontal.y / horizontalLength }
        : undefined;
    let verticalUnit = verticalLength > 1e-9
        ? { x: vertical.x / verticalLength, y: vertical.y / verticalLength }
        : undefined;
    if (!horizontalUnit && !verticalUnit) {
        horizontalUnit = { x: 1, y: 0 };
        verticalUnit = { x: 0, y: -1 };
    }
    else if (!horizontalUnit && verticalUnit) {
        horizontalUnit = { x: verticalUnit.y, y: -verticalUnit.x };
    }
    else if (horizontalUnit && !verticalUnit) {
        verticalUnit = { x: -horizontalUnit.y, y: horizontalUnit.x };
    }
    const halfHorizontal = Math.max(horizontalLength / 2, minimumEdgePixels / 2);
    const halfVertical = Math.max(verticalLength / 2, minimumEdgePixels / 2);
    const horizontalVector = {
        x: horizontalUnit.x * halfHorizontal,
        y: horizontalUnit.y * halfHorizontal,
    };
    const verticalVector = {
        x: verticalUnit.x * halfVertical,
        y: verticalUnit.y * halfVertical,
    };
    return Object.freeze([
        Object.freeze({
            x: pivot.x - horizontalVector.x - verticalVector.x,
            y: pivot.y - horizontalVector.y - verticalVector.y,
        }),
        Object.freeze({
            x: pivot.x + horizontalVector.x - verticalVector.x,
            y: pivot.y + horizontalVector.y - verticalVector.y,
        }),
        Object.freeze({
            x: pivot.x + horizontalVector.x + verticalVector.x,
            y: pivot.y + horizontalVector.y + verticalVector.y,
        }),
        Object.freeze({
            x: pivot.x - horizontalVector.x + verticalVector.x,
            y: pivot.y - horizontalVector.y + verticalVector.y,
        }),
    ]);
}
function validateScreenPoint(point) {
    return Number.isFinite(point.x) && Number.isFinite(point.y)
        ? { x: point.x, y: point.y }
        : undefined;
}
function normalizeScreenVector(vector) {
    const length = Math.hypot(vector.x, vector.y);
    return length > 1e-9
        ? { x: vector.x / length, y: vector.y / length }
        : { x: 0, y: -1 };
}
function toAdapterRejection(error, featureIds) {
    if (error instanceof SelectionTransformError) {
        return createAdapterRejection(error.code, error.message, error.featureIds.length > 0 ? error.featureIds : featureIds, error.cause);
    }
    return createAdapterRejection("SELECTION_TRANSFORM_TRANSACTION_INVALID", error instanceof Error ? error.message : "Selection transform failed.", featureIds, error);
}
function createAdapterRejection(code, message, featureIds, cause) {
    return Object.freeze({
        code,
        message,
        featureIds: Object.freeze([...featureIds]),
        ...(cause === undefined ? {} : { cause }),
    });
}
function consumeOverlayEvent(event) {
    event.preventDefault?.();
    event.stopPropagation?.();
    event.stopImmediatePropagation?.();
}
function consumePointerEvent(event) {
    event.preventDefault?.();
    event.stopPropagation?.();
    event.stopImmediatePropagation?.();
}
//# sourceMappingURL=selection-transform-interaction.js.map